import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import "./datatables.scss";

// datatable related plugins
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { CSVExport } from 'react-bootstrap-table2-toolkit';
import cellEditFactory from 'react-bootstrap-table2-editor';
import * as XLSX from 'xlsx';
const { ExportCSVButton } = CSVExport;

// eslint-disable-next-line react/prop-types
const Datatables = ({
  cellEdit = false,
  filename = '',
  expandRow = {},
  isSearch = false,
  placeholder = 'Search',
  rowsLength = 0,
  rows = [],
  handleAddButton,
  onRowClick, // Add this prop
  title,
  columns = [],
  loading,
  selectField,
  defaultSorted,
  keyField,
  btnTitle = 'Add',
  isAdd = true,
  isExcel = true,
  isTableHead = true,
  isDisabled = false,
  fas = false,
  is_remote = true,
  ssr = () => { } }
) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState(''); // State for the search input
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm); // State for the debounced input

  // Automatically translate all column text headers
  const translatedColumns = useMemo(() => {
    if (!Array.isArray(columns)) return columns || [];
    return columns.map(col => ({
      ...col,
      text: typeof col.text === 'string' ? (t(col.text) || col.text) : col.text
    }));
  }, [columns, t]);

  const isFirstRender = React.useRef(true);

  // Debounce search input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Effect to dispatch search to ssr only when debounced search term actually changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (!debouncedSearchTerm) return;
    }

    if (typeof ssr === 'function') {
      ssr(prev => {
        if (typeof prev === 'object' && prev !== null) {
          const currentVal = prev.search ?? prev.Search ?? prev.q ?? '';
          if (currentVal === debouncedSearchTerm) {
            return prev; // Return exact same reference, prevents React re-render cycle
          }
          return {
            ...prev,
            page: 1,
            limit: prev.limit || sizePerPage || 10,
            sizePerPage: prev.sizePerPage || sizePerPage || 10,
            search: debouncedSearchTerm,
            Search: debouncedSearchTerm,
            searchTerm: debouncedSearchTerm,
            q: debouncedSearchTerm
          };
        }
        return {
          page: 1,
          limit: sizePerPage,
          sizePerPage: sizePerPage,
          search: debouncedSearchTerm,
          Search: debouncedSearchTerm,
          searchTerm: debouncedSearchTerm,
          q: debouncedSearchTerm
        };
      });
    }
  }, [debouncedSearchTerm]);

  const sizePerPageList = [
    { text: '10', value: 10 },
    { text: '25', value: 25 },
    { text: '50', value: 50 },
    { text: '100', value: 100 },
    { text: t("View all") || 'All', value: rowsLength || 1000 }
  ];

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total ms-2 text-muted" style={{ fontSize: '0.84rem', fontWeight: 500 }}>
      {t("Showing")} <strong className="text-dark">{from}</strong> {t("to")} <strong className="text-dark">{to}</strong> {t("of")} <strong className="text-dark">{size}</strong> {t("entries")}
    </span>
  );

  const customSizePerPage = ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="d-inline-flex align-items-center">
      <select
        className="form-select form-select-sm"
        value={currSizePerPage}
        onChange={(e) => onSizePerPageChange(Number(e.target.value))}
        style={{
          height: '32px',
          padding: '0 24px 0 10px',
          borderRadius: '6px',
          borderColor: '#cbd5e1',
          fontSize: '0.84rem',
          fontWeight: '600',
          color: '#1e293b',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          width: '72px',
          minWidth: '72px'
        }}
      >
        {options.map(opt => (
          <option key={opt.text} value={opt.page}>
            {opt.text}
          </option>
        ))}
      </select>
    </div>
  );

  const pageOptions = {
    sizePerPage: sizePerPage,
    totalSize: rowsLength,
    page: page || 1,
    hideSizePerPage: false,
    showTotal: true,
    paginationTotalRenderer: customTotal,
    sizePerPageRenderer: customSizePerPage,
    sizePerPageList: sizePerPageList,
  };

  const handleTableChange = (type, { page: newPage, sizePerPage: newSize }) => {
    setPage(newPage);
    setSizePerPage(newSize);
    if (typeof ssr === 'function') {
      ssr(prev => {
        if (typeof prev === 'object' && prev !== null) {
          if (prev.page === newPage && (prev.limit === newSize || prev.sizePerPage === newSize)) {
            return prev;
          }
          return {
            ...prev,
            page: newPage,
            limit: newSize,
            sizePerPage: newSize,
            search: debouncedSearchTerm,
            Search: debouncedSearchTerm,
            searchTerm: debouncedSearchTerm,
            q: debouncedSearchTerm
          };
        }
        return {
          page: newPage,
          limit: newSize,
          sizePerPage: newSize,
          search: debouncedSearchTerm,
          Search: debouncedSearchTerm,
          searchTerm: debouncedSearchTerm,
          q: debouncedSearchTerm
        };
      });
    }
  };

  const safeRows = Array.isArray(rows)
    ? rows
    : (Array.isArray(rows?.items) ? rows.items : (Array.isArray(rows?.rows) ? rows.rows : []));

  const getCleanFileName = (name, fallbackTitle = 'export', ext = '.xlsx') => {
    let base = (name || fallbackTitle || 'export_data').toString().trim();
    base = base.replace(/[/\\?%*:|"<>]/g, '_').trim();
    if (!base) base = 'export_data';
    base = base.replace(/\.(csv|xlsx|txt)$/i, '');
    return `${base}${ext}`;
  };

  const handleExportExcel = (fallbackCsvFn) => {
    try {
      const exportableCols = (translatedColumns || []).filter(c => c.csvExport !== false && c.dataField);
      const dataToExport = (safeRows || []).map((row, rIdx) => {
        const rowObj = {};
        exportableCols.forEach(col => {
          const header = col.text || col.dataField;
          let val = row[col.dataField];
          if (typeof col.csvFormatter === 'function') {
            val = col.csvFormatter(val, row, rIdx, col.formatExtraData);
          }
          if (val === undefined || val === null) val = '';
          if (typeof val === 'object') {
            val = val.label || val.title || val.name || val.value || JSON.stringify(val);
          }
          rowObj[header] = val;
        });
        return rowObj;
      });

      if (XLSX && XLSX.utils) {
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        const exportName = getCleanFileName(filename, title || 'export', '.xlsx');
        XLSX.writeFile(workbook, exportName);
        return;
      }
    } catch (err) {
      console.warn("Excel XLSX export fallback to CSV:", err);
    }

    if (typeof fallbackCsvFn === 'function') {
      fallbackCsvFn();
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          {
            loading ?
              <div style={{ height: '160px' }}>
                <div id="status" style={{ height: '100vh', width: 'auto' }}>
                  <div className="spinner-chase">
                    <div className="chase-dot"></div>
                    <div className="chase-dot"></div>
                    <div className="chase-dot"></div>
                    <div className="chase-dot"></div>
                    <div className="chase-dot"></div>
                    <div className="chase-dot"></div>
                  </div>
                  <div style={{ margin: '30px auto', textAlign: 'center' }}>{t("Loading")}</div>
                </div>
                <div style={{ margin: '30px auto', textAlign: 'center' }}>{t("Loading")}</div>
              </div> :
              <ToolkitProvider
                keyField={keyField}
                data={safeRows}
                columns={translatedColumns}
                search
                exportCSV={{
                  fileName: getCleanFileName(filename, title || 'export', '.csv'),
                  blobType: 'text/csv;charset=utf-8;',
                  noAutoBOM: false,
                  exportAll: true
                }}
              >
                {props => (
                  <div>
                    {
                      isTableHead ?
                        <div className="datatable-header-toolbar d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                          <h5 className="datatable-header-title font-weight-bold text-slate-800 mb-0">
                            {typeof title === 'string' ? (t(title) || title) : title}
                          </h5>

                          {selectField}

                          <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
                            {
                              isSearch ?
                                <div className="datatable-search-wrapper position-relative">
                                  <input
                                    className="form-control datatable-search-input pe-4"
                                    placeholder={t(placeholder) || placeholder || `${t("Search")}...`}
                                    value={searchTerm}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setSearchTerm(val);
                                      if (props?.searchProps?.onSearch) {
                                        props.searchProps.onSearch(val);
                                      }
                                    }}
                                  />
                                  {searchTerm ? (
                                    <button
                                      type="button"
                                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted p-0 me-2 border-0 bg-transparent"
                                      onClick={() => {
                                        setSearchTerm('');
                                        if (props?.searchProps?.onSearch) {
                                          props.searchProps.onSearch('');
                                        }
                                      }}
                                      title="Clear search"
                                      style={{ zIndex: 5, fontSize: '14px', lineHeight: 1, textDecoration: 'none' }}
                                    >
                                      ✕
                                    </button>
                                  ) : (
                                    <i className="bx bx-search-alt datatable-search-icon" />
                                  )}
                                </div> : ''
                            }
                            {isExcel && (
                              <button
                                type="button"
                                className="btn datatable-btn-csv"
                                onClick={() => handleExportExcel(props?.csvProps?.onExport)}
                                title={t("Export to Excel (.xlsx / .csv)") || "Export to Excel (.xlsx / .csv)"}
                              >
                                <i className="fas fa-file-excel" />
                              </button>
                            )}
                            {
                              isAdd ?
                                <Button
                                  disabled={isDisabled}
                                  color="primary"
                                  className="datatable-btn-add"
                                  onClick={handleAddButton}
                                  onKeyDown={handleAddButton}
                                >
                                  <i className={`fas ${fas ? fas : "fa-plus"} me-1`} />
                                  <span>{t(btnTitle) || btnTitle}</span>
                                </Button> : ''
                            }
                          </div>
                        </div> : ''
                    }
                    <div className="table-responsive mt-2.5">
                      {loading ? (
                        <div className="text-center my-4 py-3">
                          <span className="spinner-border spinner-border-sm text-warning me-2" role="status" />
                          <span className="text-muted font-size-13">{t("Loading records...")}</span>
                        </div>
                      ) :
                        safeRows.length == 0 ? (
                          <div className="text-center py-5 text-muted font-size-13">
                            <i className="bx bx-folder-open font-size-28 d-block mb-1 text-muted opacity-50" />
                            {t("No records found")}
                          </div>
                        ) : (
                          <BootstrapTable
                            {...props.baseProps}
                            bordered={false}
                            expandRow={expandRow}
                            striped={false}
                            classes={`table align-middle table-nowrap mb-0 ${onRowClick ? 'table-hover cursor-pointer' : ''}`}
                            headerWrapperClasses={"thead-light"}
                            remote={is_remote}
                            sort={defaultSorted}
                            pagination={paginationFactory(pageOptions)}
                            onTableChange={handleTableChange}
                            cellEdit={cellEdit ? cellEditFactory({
                              mode: 'click',
                              blurToSave: true,
                              afterSaveCell: (oldValue, newValue, row, column) => {
                                console.log("Changed:", {
                                  field: column.dataField,
                                  oldValue,
                                  newValue,
                                  row
                                });
                                updateSttate(row.id, newValue);
                              }
                            }) : undefined}
                            rowEvents={onRowClick ? {
                              onClick: (e, row) => onRowClick(row)
                            } : {}}
                          />
                        )
                      }
                    </div>
                  </div>
                )}
              </ToolkitProvider>
          }
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default Datatables
